import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import { topicsAPI } from "../api/index.js";
import SEO from "../components/SEO.jsx";
import { TOPIC_DESCRIPTIONS } from "../constants/topics.js";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  purple:"#7C6FFF", danger:"#FF6B6B", warn:"#FFB347",
  font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const FEATURES = [
  { icon:"❓", title:"Ask Questions", desc:"Get real answers from a knowledgeable community. No bots, no spam." },
  { icon:"⭐", title:"Review Brokers", desc:"Read and write honest reviews on forex brokers, crypto exchanges and investment platforms." },
  { icon:"🚨", title:"Report Scams", desc:"Warn others about fraudulent brokers and investment scams. Protect the community." },
  { icon:"🔍", title:"Research Listings", desc:"Search and compare regulated brokers before you invest your money." },
  { icon:"✨", title:"Personalized Feed", desc:"Follow topics and get a feed tailored to your interests." },
  { icon:"🔔", title:"Stay Notified", desc:"Get notified when someone answers your question or comments on your post." },
];

const STATS = [
  { value:"10+", label:"Topics" },
  { value:"5+",  label:"Listed Brokers" },
  { value:"100%", label:"Community Driven" },
  { value:"Free", label:"Always" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    if (user) navigate("/feed");
  }, [user, navigate]);

  useEffect(() => { topicsAPI.list().then(r => setTopics(r.data.topics || [])).catch(() => {}); }, []);

  return (
    <div style={{ background:G.bg, minHeight:"100vh", fontFamily:G.font, color:G.text }}>
      <SEO
        description="askFOGRA is a community Q&A platform where you can ask questions, get real answers, review brokers, and report financial scams."
        url="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "askFOGRA",
          "url": "https://askfogra.com",
          "description": "A community Q&A platform where you can ask questions, get real answers, review brokers, and report financial scams.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": { "@type": "EntryPoint", "urlTemplate": "https://askfogra.com/feed?search={search_term_string}" },
            "query-input": "required name=search_term_string"
          }
        }}
      />

      {/* Navbar */}
      <nav style={{ background:"#0D0D0D", borderBottom:`1px solid ${G.border}`, padding:"0 24px", display:"flex", alignItems:"center", justifyContent:"space-between", height:56, position:"sticky", top:0, zIndex:100 }}>
        <span style={{ fontSize:22, letterSpacing:-1, fontFamily:G.font }}>
          <span style={{ fontWeight:900, color:G.accent }}>ask</span>
          <span style={{ fontWeight:700, color:G.text }}>FOGRA</span>
        </span>
        <div style={{ display:"flex", gap:10 }}>
          <Link to="/login" style={{ background:"#1A1A1A", border:`1px solid #2A2A2A`, color:G.muted, padding:"7px 18px", borderRadius:24, textDecoration:"none", fontSize:13, fontFamily:G.mono, transition:"all 0.15s" }}>Login</Link>
          <Link to="/register" style={{ background:G.accent, color:"#000", padding:"7px 18px", borderRadius:24, textDecoration:"none", fontSize:13, fontFamily:G.mono, fontWeight:700, transition:"all 0.15s" }}>Sign Up Free</Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="landing-hero" style={{ maxWidth:860, margin:"0 auto", padding:"80px 24px 60px", textAlign:"center" }}>
        <div style={{ display:"inline-block", background:G.accent+"15", border:`1px solid ${G.accent}44`, color:G.accent, fontSize:12, padding:"4px 14px", borderRadius:20, fontFamily:G.mono, letterSpacing:1, marginBottom:24 }}>
          NOW IN BETA
        </div>
        <h1 style={{ fontSize:52, fontWeight:800, color:G.text, lineHeight:1.15, letterSpacing:-2, marginBottom:20 }}>
          Ask. Answer.<br />
          <span style={{ color:G.accent }}>Know.</span>
        </h1>
        <p style={{ fontSize:18, color:G.muted, lineHeight:1.7, maxWidth:580, margin:"0 auto 36px" }}>
          askFOGRA is a community Q&A platform where you can get real answers, review brokers, and protect others from financial scams.
        </p>
        <div className="landing-buttons" style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
          <Link to="/register" style={{ background:G.accent, color:"#000", padding:"13px 32px", borderRadius:28, textDecoration:"none", fontSize:15, fontFamily:G.mono, fontWeight:700, transition:"opacity 0.15s" }}
            onMouseOver={e => e.currentTarget.style.opacity="0.85"}
            onMouseOut={e => e.currentTarget.style.opacity="1"}>
            Get Started Free →
          </Link>
          <Link to="/login" style={{ background:"#1A1A1A", border:`1px solid #2A2A2A`, color:G.muted, padding:"13px 32px", borderRadius:28, textDecoration:"none", fontSize:15, fontFamily:G.mono, transition:"all 0.15s" }}>
            Login
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div style={{ maxWidth:860, margin:"0 auto", padding:"0 24px 60px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:"20px 16px", textAlign:"center" }}>
              <div style={{ fontSize:28, fontWeight:800, color:G.accent, fontFamily:G.mono }}>{s.value}</div>
              <div style={{ fontSize:12, color:G.muted, marginTop:4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div style={{ maxWidth:860, margin:"0 auto", padding:"0 24px 80px" }}>
        <h2 style={{ fontSize:32, fontWeight:700, color:G.text, textAlign:"center", marginBottom:40, letterSpacing:-1 }}>
          Everything you need in one place
        </h2>
        <div className="landing-features" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:14, padding:24, transition:"border-color 0.2s" }}
              onMouseOver={e => e.currentTarget.style.borderColor="#2A2A2A"}
              onMouseOut={e => e.currentTarget.style.borderColor=G.border}>
              <div style={{ fontSize:28, marginBottom:12 }}>{f.icon}</div>
              <h3 style={{ fontSize:16, fontWeight:700, color:G.text, marginBottom:8 }}>{f.title}</h3>
              <p style={{ fontSize:13, color:G.muted, lineHeight:1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Topics */}
      {topics.length > 0 && (
        <div style={{ maxWidth:1000, margin:"0 auto", padding:"0 24px 80px" }}>
          <h2 style={{ fontSize:32, fontWeight:700, color:G.text, textAlign:"center", marginBottom:12, letterSpacing:-1 }}>
            Browse by Topic
          </h2>
          <p style={{ color:G.muted, fontSize:14, textAlign:"center", marginBottom:36 }}>
            Explore questions and answers across {topics.length} topics
          </p>
          <div className="landing-topics" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
            {topics.map(t => (
              <Link key={t.id} to={`/topics/${t.slug}`} style={{ textDecoration:"none" }}>
                <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:14, padding:20, height:"100%", boxSizing:"border-box", transition:"border-color 0.2s" }}
                  onMouseOver={e => e.currentTarget.style.borderColor="#2A2A2A"}
                  onMouseOut={e => e.currentTarget.style.borderColor=G.border}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                    <span style={{ width:10, height:10, borderRadius:"50%", background:t.color||G.accent, display:"inline-block", flexShrink:0 }} />
                    <h3 style={{ fontSize:15, fontWeight:700, color:G.text, margin:0 }}>{t.name}</h3>
                  </div>
                  <p style={{ fontSize:12, color:G.muted, lineHeight:1.6, marginBottom:10 }}>{TOPIC_DESCRIPTIONS[t.slug] || t.description}</p>
                  <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{t.question_count} questions</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{ background:G.surface, borderTop:`1px solid ${G.border}`, padding:"60px 24px", textAlign:"center" }}>
        <h2 style={{ fontSize:32, fontWeight:700, color:G.text, marginBottom:12, letterSpacing:-1 }}>
          Ready to join the community?
        </h2>
        <p style={{ color:G.muted, fontSize:15, marginBottom:28 }}>Free forever. No credit card required.</p>
        <Link to="/register" style={{ background:G.accent, color:"#000", padding:"13px 36px", borderRadius:28, textDecoration:"none", fontSize:15, fontFamily:G.mono, fontWeight:700 }}>
          Create Free Account →
        </Link>
      </div>

      {/* Footer */}
      <div style={{ borderTop:`1px solid ${G.border}`, padding:"24px", textAlign:"center" }}>
        <span style={{ fontSize:14, letterSpacing:-0.5, fontFamily:G.font }}>
          <span style={{ fontWeight:900, color:G.accent }}>ask</span>
          <span style={{ fontWeight:700, color:G.text }}>FOGRA</span>
        </span>
        <span style={{ fontSize:12, color:"#333", marginLeft:16, fontFamily:G.mono }}>© {new Date().getFullYear()} askFOGRA. All rights reserved.</span>
      </div>
    </div>
  );
}
